/**
 * Confirmation modal before fetching lead counts from SMSWiser.
 * Shows estimated time and list count for a professional UX.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Clock, Info, RefreshCw, X } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import {
  estimateFetchSeconds,
  formatEstimatedDuration,
} from '@/features/smswiser-catalog/utils/estimateFetchDuration';
import { backdropClick } from '@/components/ui';

export interface FetchLeadCountConfirmModalProps {
  listCount: number;
  onClose: () => void;
  onConfirm: () => void;
  isBusy: boolean;
}

export const FetchLeadCountConfirmModal: FC<FetchLeadCountConfirmModalProps> = ({
  listCount,
  onClose,
  onConfirm,
  isBusy,
}) => {
  const estimatedSeconds = estimateFetchSeconds(listCount);
  const estimatedDuration = formatEstimatedDuration(estimatedSeconds);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 p-4 backdrop-blur-sm"
      onClick={backdropClick(!isBusy ? onClose : undefined)}
    >
      <div
        className="relative w-full max-w-md rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border bg-muted/20 px-4 py-3">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            <RefreshCw className="h-4 w-4" />
            Fetch lead count
          </h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClose}
            disabled={isBusy}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4 p-4">
          <p className="text-sm text-muted-foreground">
            Fetch current lead counts from SMSWiser for {listCount} selected list
            {listCount !== 1 ? 's' : ''}. This will update the displayed counts
            and remaining leads.
          </p>

          <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/10 p-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Clock className="h-4 w-4 shrink-0 text-primary" />
              Estimated duration: {estimatedDuration}
            </div>
            <p className="text-xs text-muted-foreground">
              Each list requires a separate API call to SMSWiser. Please keep this
              tab open until the operation completes.
            </p>
          </div>

          <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/5 p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">
              After fetching, counts will appear in the list view and remaining
              leads will be calculated as total minus today&apos;s usage (drafts
              and sent).
            </p>
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-border bg-muted/10 px-4 py-3">
          <Button variant="outline" onClick={onClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isBusy}
            className="gap-2"
            aria-busy={isBusy}
          >
            {isBusy ? (
              <>
                <LoadingSpinner size="sm" />
                Fetching…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Start fetch
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
